import React, { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import moment from "moment";
import { LuArrowDown, LuArrowUp, LuClock, LuSearch } from "react-icons/lu";
import Seo from "../components/Seo";
import DownloadButton from "../components/DownloadButton";
import NotFound from "./NotFound";
import { useMarkets, useResults } from "../hooks/useData";
import { BRAND, siteUrl } from "../utils/config";
import { buildWeeks, DAY_LABELS, isDouble, titleCase } from "../utils/data";

const cellText = (d) => `${d.open} ${d.jodi} ${d.close}`;

const Digits = ({ value }) => (
  <span className="rt-pana__stack">
    {String(value || "***")
      .split("")
      .map((c, i) => (
        <b key={i}>{c}</b>
      ))}
  </span>
);

const MarketChart = ({ kind }) => {
  const { slug } = useParams();
  const { markets, loading: mLoading, error: mError } = useMarkets();
  const { bySlug, loading: rLoading, error } = useResults();
  const [query, setQuery] = useState("");

  const market = markets.find((m) => m.slug === slug);
  const weeks = useMemo(() => buildWeeks(bySlug[slug]), [bySlug, slug]);
  const q = query.trim().toLowerCase();

  if (!mLoading && !market && mError) {
    return (
      <main className="rt-main">
        <section className="rt-wrap rt-section" style={{ textAlign: "center" }}>
          <p className="rt-empty">Markets could not be loaded right now. Please check your connection and try again.</p>
          <button type="button" className="rt-retry" onClick={() => window.location.reload()}>
            Try again
          </button>
        </section>
      </main>
    );
  }
  if (!mLoading && !market) {
    // close match such as /jodi/kalyan-chart or /jodi/kalyan-night-matka -> send to the real page
    const alt = markets.find((m) => slug.startsWith(`${m.slug}-`) || m.slug.startsWith(`${slug}-`));
    return alt ? <Navigate to={`/${kind}/${alt.slug}`} replace /> : <NotFound markets={markets} />;
  }
  if (!market) return <main className="rt-main"><p className="rt-empty">Loading chart…</p></main>;

  const isJodi = kind === "jodi";
  const name = titleCase(market.name);
  const kindLabel = isJodi ? "Jodi" : "Pana";
  const other = isJodi ? "pana" : "jodi";
  const path = `/${kind}/${slug}`;
  const origin = siteUrl();
  const title = isJodi
    ? `${name} Jodi Chart – ${name} Satta Matka Record | ${BRAND.name}`
    : `${name} Pana Chart – ${name} Panel Matka Record | ${BRAND.name}`;
  const description = `${name} ${kindLabel} chart: satta matka result record of every day, Monday to Sunday, updated live. See ${name} matka timings (${market.openTime} - ${market.closeTime}), open pana, close pana and jodi on ${BRAND.name}.`;

  const faqs = [
    {
      q: `What is the ${name} ${kindLabel} chart?`,
      a: isJodi
        ? `The ${name} Jodi chart lists the two-digit jodi declared by ${name} for every day, grouped week by week from Monday to Sunday.`
        : `The ${name} Pana chart shows the open pana, jodi and close pana of ${name} for every day, grouped week by week from Monday to Sunday.`,
    },
    {
      q: `What are the ${name} timings?`,
      a: `${name} opens at ${market.openTime} and closes at ${market.closeTime}.`,
    },
    {
      q: `How is the ${name} jodi calculated?`,
      a: `Add the three digits of the open pana and keep the last digit; do the same for the close pana. Joined together, the two digits form the jodi.`,
    },
  ];

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "All Charts", item: `${origin}/charts` },
        { "@type": "ListItem", position: 3, name: `${name} ${kindLabel} Chart`, item: `${origin}${path}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  const hit = (d) => !!q && !!d && cellText(d).toLowerCase().includes(q);
  const days = bySlug[slug] || {};
  const lastKey = Object.keys(days).sort().pop();

  return (
    <main className="rt-main">
      <Seo title={title} description={description} path={path} schema={schema} />

      <section className="rt-phead">
        <div className="rt-wrap">
          <nav className="rt-crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/charts">All Charts</Link>
            <span>/</span>
            <b>
              {name} {kindLabel}
            </b>
          </nav>
          <span className="rt-eyebrow rt-eyebrow--dark">{kindLabel} Chart</span>
          <h1>
            {name} {kindLabel} Chart
          </h1>
          <p>
            Official historical {market.name} records, updated live.{" "}
            <span className="rt-phead__time">
              <LuClock size={14} aria-hidden="true" /> {market.openTime} - {market.closeTime}
            </span>
          </p>
          <div className="rt-tabs rt-tabs--lg">
            <Link to={`/jodi/${slug}`} className={isJodi ? "is-on" : ""}>
              Jodi Chart
            </Link>
            <Link to={`/pana/${slug}`} className={!isJodi ? "is-on" : ""}>
              Pana Chart
            </Link>
          </div>
        </div>
      </section>

      <section className="rt-wrap rt-chart">
        <div className="rt-chart__bar">
          <h2>
            {market.name} {kindLabel.toUpperCase()} RECORD
          </h2>
          <label className="rt-search">
            <span className="rt-sr">Find a number in the chart</span>
            <LuSearch size={16} aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Find jodi / pana"
              inputMode="numeric"
            />
          </label>
        </div>

        {rLoading ? (
          <p className="rt-empty">Loading chart…</p>
        ) : error ? (
          <p className="rt-empty">Chart data is not available right now. Please try again shortly.</p>
        ) : (
          <div className="rt-tablewrap">
            <table className="rt-table">
              <caption className="rt-sr">
                {name} {kindLabel} chart, week by week
              </caption>
              <thead>
                <tr>
                  <th scope="col">Date range</th>
                  {DAY_LABELS.map((d) => (
                    <th key={d} scope="col">
                      {d}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {weeks.length === 0 && (
                  <tr>
                    <td colSpan={8} className="rt-table__empty">
                      No records available yet.
                    </td>
                  </tr>
                )}
                {weeks.map((w) => (
                  <tr key={w.key}>
                    <th scope="row" className="rt-table__date">
                      <span>{w.from}</span>
                      <i>-</i>
                      <span>{w.to}</span>
                    </th>
                    {w.cells.map((d, i) => {
                      const jodi = d ? d.jodi : "";
                      const red = isDouble(jodi);
                      return (
                        <td key={i} className={`${hit(d) ? "is-hit" : ""}${red ? " is-red" : ""}`}>
                          {!d ? null : isJodi ? (
                            <span className="rt-jodi">{jodi}</span>
                          ) : (
                            <div className="rt-pana">
                              <Digits value={d.open} />
                              <span className="rt-pana__jodi">{jodi}</span>
                              <Digits value={d.close} />
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="rt-howto">
          <strong>How to read:</strong> every row is one week (Monday to Sunday).{" "}
          {isJodi
            ? "Each cell is that day's two-digit jodi."
            : "Each cell shows the open pana on top, the jodi in the middle and the close pana below."}{" "}
          Red means both digits are the same. XX, ** or *** means that part is not declared yet.
          {lastKey && <> Latest data: {moment(lastKey).format("DD MMM YYYY")}.</>}
        </p>

        <div className="rt-chart__actions">
          <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <LuArrowUp size={16} aria-hidden="true" /> Go to top
          </button>
          <button
            type="button"
            onClick={() => document.getElementById("related")?.scrollIntoView({ behavior: "smooth" })}
          >
            <LuArrowDown size={16} aria-hidden="true" /> Other charts
          </button>
        </div>
      </section>

      <section className="rt-wrap rt-cta-row">
        <DownloadButton />
      </section>

      <section className="rt-section" id="related" aria-labelledby="rel-h">
        <div className="rt-wrap">
          <h2 className="rt-title rt-title--sm" id="rel-h">
            More {kindLabel} Charts
          </h2>
          <div className="rt-pills">
            {markets
              .filter((m) => m.slug !== slug)
              .map((m) => (
                <Link key={m.slug} to={`/${kind}/${m.slug}`}>
                  {titleCase(m.name)} {kindLabel}
                </Link>
              ))}
            <Link to={`/${other}/${slug}`} className="is-accent">
              {name} {other === "jodi" ? "Jodi" : "Pana"}
            </Link>
          </div>
        </div>
      </section>

      <section className="rt-section rt-section--tint">
        <div className="rt-wrap rt-prose">
          <h2>
            {name} {kindLabel} Chart — FAQ
          </h2>
          {faqs.map((f) => (
            <div key={f.q}>
              <h3>{f.q}</h3>
              <p>{f.a}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default MarketChart;
